import { useEffect, useState } from "react";
import setThemeColor from "../../utils/setThemeColor/setThemeColor";
import { Grid } from "../../components/feature/Grid/Grid";
import { NavMenu } from "../../components/feature/NavMenu/NavMenu";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { IOptions } from "../../components/feature/NavMenu/elements/AdditionalOptionsMenu/AdditionalOptionsMenu";
import styles from "./ExplorePage.module.css";
import { setIsWaiting } from "../../redux/actions/appActions";
import { useTranslation } from "react-i18next";
import Search from "../../components/feature/Search/Search";
import { IFilters } from "../../components/feature/Search/elements/FiltersMenu/FiltersMenu";
import setWebPageTitle from "../../utils/setWebPageTitle/setWebPageTitle";
import setWebPagePreviewImage from "../../utils/setWebPagePreviewImage/setWebPagePreviewImage";

const ExplorePage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const searchEndpoint = "pageofrecipes/explore/search";
    const defaultEndpoint = "pageofrecipes/explore";
    const isMobile = useSelector((state: RootState) => state.client.isMobile);
    const isLoggedIn = useSelector((state: RootState) => state.client.isLoggedIn);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [endpoint, setEndpoint] = useState(defaultEndpoint);
    const [options, setOptions] = useState<IOptions[]>([]);
    const [filters, setFilters] = useState<IFilters>({
        minPublicationDateTime: null,
        maxPublicationDateTime: null,
        minPreparationTime: null,
        maxPreparationTime: null,
        isPublic: null,
        isFromExplore: null,
        languageId: null,
        tags: null,
        searchQuery: null,
    });

    const triggerAction = (action: string) => {
        if (action === "SEARCH") {
            setIsSearchActive(!isSearchActive);
        }
    };

    const onFiltersSelected = (filters: IFilters) => {
        setFilters(filters);
    };

    useEffect(() => {
        if (isSearchActive) {
            setEndpoint(searchEndpoint);
        } else {
            setEndpoint(defaultEndpoint);
        }
    }, [isSearchActive]);

    useEffect(() => {
        if (isLoggedIn) {
            setOptions([
                {
                    link: "/",
                    name: t("containers.myRecipesPage.title"),
                    icon: <i className="bi bi-collection"></i>,
                },
                {
                    link: "/add",
                    name: t("containers.addRecipePage.title"),
                    icon: <i className="bi bi-plus-circle"></i>,
                },
                {
                    link: "/settings",
                    name: t("containers.settingsPage.title"),
                    icon: <i className="bi bi-gear"></i>,
                },
                {
                    link: "/info",
                    name: t("containers.infoPage.title"),
                    icon: <i className="bi bi-info-circle"></i>,
                },
            ]);
        } else {
            setOptions([
                {
                    link: "/settings",
                    name: t("containers.settingsPage.title"),
                    icon: <i className="bi bi-gear"></i>,
                },
                {
                    link: "/info",
                    name: t("containers.infoPage.title"),
                    icon: <i className="bi bi-info-circle"></i>,
                },
            ]);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        setThemeColor("#ffffff");
        setWebPageTitle();
        setWebPagePreviewImage();
        dispatch(setIsWaiting(true));
    }, []);

    return (
        <div>
            <NavMenu
                options={options}
                isSearchActive={true}
                triggerAction={triggerAction}
                title={
                    <span>
                        <i className="bi bi-globe-americas"></i> {t("containers.explorePage.title")}
                    </span>
                }
            />
            {isMobile && !isSearchActive && (
                <p className={styles.title}>
                    <i className="bi bi-globe-americas"></i> {t("containers.explorePage.title")}
                </p>
            )}
            {isSearchActive && <Search selectedFilters={onFiltersSelected} isExploreMode={true} />}
            {/* When search is disabled */}
            {endpoint === defaultEndpoint && <Grid endpoint={endpoint} />}
            {/* When search is enabled */}
            {endpoint === searchEndpoint &&
                (filters.searchQuery === undefined ||
                    filters.searchQuery == null ||
                    filters?.searchQuery[0] !== "@") && <Grid endpoint={endpoint} filters={filters} />}
            {/* When search is enabled and looking for user */}
            {endpoint === searchEndpoint && filters?.searchQuery && filters?.searchQuery[0] === "@" && (
                <Grid endpoint="pageofusers" itemType="user" filters={filters} />
            )}
        </div>
    );
};

export default ExplorePage;
